import Vue from 'vue';
import router from '../../router/';
import {
  AUTH_LOGIN,
  AUTH_LOGOUT,
  CHANGE_USER_WORK_STATUS,
  CREATE_USER,
  DISABLE_2FA,
  EDIT_BALANCE,
  EDIT_SUPPORT_LIMIT,
  EDIT_USER,
  ENABLE_2FA,
  GET_2FA_DATA,
  LOAD_USERS_LIST,
  RESET_SUPPORT_LIMIT,
} from '../const/user';
import { HTTP_DELETE, HTTP_GET, HTTP_POST, HTTP_PUT } from '../const';
import { resetSessionState } from '@/store/resetSession';
import { clearListFilters } from '@/utils/listFilters';

export default {
  namespaced: true,
  state: {
    id: localStorage.getItem('id') || '',
    token: localStorage.getItem('token') || '',
    username: localStorage.getItem('username') || '',
    role: localStorage.getItem('role') || '',
    canAddWithdraws: localStorage.getItem('canAddWithdraws') === 'true',
    users: [],
    roles: {
      client: 'Клиент',
      support: 'Поддержка',
      service: 'Служба',
      admin: 'Админ',
      superadmin: 'Суперадмин',
    },
  },
  getters: {
    getRole: (state) => state.role,
    getToken: (state) => state.token,
    isAdmin: (state) => state.role === 'admin' || state.role === 'superadmin',
    isSuperadmin: (state) => state.role === 'superadmin',
    isSupport: (state) => state.role === 'support',
    isPartner: (state) => state.role === 'partner',
    users: (state) => state.users,
    username: (state) => state.username,
    roles: (state) => state.roles,
    canAddWithdraws: (state) => state.canAddWithdraws,
  },
  actions: {
    [AUTH_LOGIN]: async ({ dispatch, commit }, { name, password, data }) => {
      const request = await dispatch(
        HTTP_POST,
        {
          method: AUTH_LOGIN,
          body: { name, password, data },
          with_token: false,
          no_commit: true,
          namespace: 'user',
        },
        { root: true },
      );

      if (request && request.data) {
        const data = request.data;
        const token = data.token;
        const user = data.user;
        resetSessionState(commit);
        localStorage.setItem('token', token);
        localStorage.setItem('username', user.name);
        localStorage.setItem('role', user.role);
        localStorage.setItem('canAddWithdraws', user.can_add_withdraws);

        commit(AUTH_LOGIN, {
          token,
          username: user.name,
          role: user.role,
          canAddWithdraws: user.can_add_withdraws,
        });
        clearListFilters();
        await router.push({ name: 'Deposits' });
      }
    },
    [AUTH_LOGOUT]: async ({ dispatch, commit }) => {
      const request = await dispatch(
        HTTP_POST,
        {
          method: AUTH_LOGOUT,
          no_commit: true,
        },
        { root: true },
      ).catch(() => null);
      if (!request?.success) {
        return request || {
          success: false,
          message: 'Не удалось завершить сессию на сервере',
        };
      }
      resetSessionState(commit);
      await router.push({ name: 'Login' });
      return request;
    },
    [LOAD_USERS_LIST]: async ({ dispatch }) => {
      await dispatch(
        HTTP_GET,
        {
          method: LOAD_USERS_LIST,
          namespace: 'user',
        },
        { root: true },
      );
    },
    [CREATE_USER]: async ({ dispatch }, user) => {
      return await dispatch(
        HTTP_POST,
        {
          method: CREATE_USER,
          namespace: 'user',
          body: user,
          no_commit: true,
        },
        { root: true },
      );
    },
    [EDIT_USER]: async ({ dispatch }, { name, data }) => {
      return await dispatch(
        HTTP_PUT,
        {
          method: EDIT_USER,
          namespace: 'user',
          body: data,
          replace: { name },
          no_commit: true,
        },
        { root: true },
      );
    },
    [EDIT_BALANCE]: async ({ dispatch }, { id, data }) => {
      return await dispatch(
        HTTP_PUT,
        {
          method: EDIT_BALANCE,
          namespace: 'user',
          body: data,
          replace: { id },
          no_commit: true,
        },
        { root: true },
      );
    },
    [CHANGE_USER_WORK_STATUS]: async ({ dispatch }, { data, status }) => {
      return await dispatch(
        HTTP_PUT,
        {
          method: CHANGE_USER_WORK_STATUS,
          namespace: 'user',
          replace: { status },
          body: data,
          no_commit: true,
        },
        { root: true },
      );
    },
    [EDIT_SUPPORT_LIMIT]: async ({ dispatch }, { id, data }) => {
      return await dispatch(
        HTTP_PUT,
        {
          method: EDIT_SUPPORT_LIMIT,
          namespace: 'user',
          body: data,
          replace: { id },
          no_commit: true,
        },
        { root: true },
      );
    },
    [RESET_SUPPORT_LIMIT]: async ({ dispatch }, { id }) => {
      return await dispatch(
        HTTP_PUT,
        {
          method: RESET_SUPPORT_LIMIT,
          namespace: 'user',
          replace: { id },
          no_commit: true,
        },
        { root: true },
      );
    },
    [GET_2FA_DATA]: async ({ dispatch }) => {
      return await dispatch(
        HTTP_GET,
        {
          method: GET_2FA_DATA,
          namespace: 'user',
          no_commit: true,
        },
        { root: true },
      );
    },
    [ENABLE_2FA]: async ({ dispatch }, { token, secret }) => {
      return await dispatch(
        HTTP_POST,
        {
          method: ENABLE_2FA,
          namespace: 'user',
          body: { token, secret },
          no_commit: true,
        },
        { root: true },
      );
    },
    [DISABLE_2FA]: async ({ dispatch }, { token }) => {
      return await dispatch(
        HTTP_DELETE,
        {
          method: DISABLE_2FA,
          namespace: 'user',
          body: { token },
          no_commit: true,
        },
        { root: true },
      );
    },
  },
  mutations: {
    [AUTH_LOGIN]: (state, { token, username, role, canAddWithdraws }) => {
      Vue.set(state, 'token', token);
      Vue.set(state, 'username', username);
      Vue.set(state, 'role', role);
      Vue.set(state, 'canAddWithdraws', canAddWithdraws);
    },
    [AUTH_LOGOUT]: (state) => {
      localStorage.clear();
      Vue.set(state, 'token', '');
      Vue.set(state, 'username', '');
      Vue.set(state, 'role', '');
      Vue.set(state, 'canAddWithdraws', false);
      Vue.set(state, 'users', []);
    },
    [LOAD_USERS_LIST]: (state, { data }) => {
      if (data) {
        Vue.set(state, 'users', data);
      }
    },
  },
};
